// src/tabs/messages/InboxPanel.jsx — Phase 2.3 (ADR-026)
// Inbox unifié : liste des threads (gauche) + thread sélectionné (droite).
// Sources : inbox_messages (alimenté par cron Beds24 polling + webhook Resend).

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Inbox as InboxIcon, RefreshCw, CheckCircle2, AlertTriangle, Home,
  FileText, Clock3, Mail, MessageSquare, MessageCircle, History,
  ArrowLeft,
} from "lucide-react";
import { supabase } from "../../supabase";
import { DS } from "../../lib/ui";
import InboxThread from "./InboxThread";
// Mini-lot Journal (Dimitri) : le point d'entrée du Journal (ADR-268, lecture
// seule) déménage du sous-onglet vers un BOUTON en tête de la page Message.
// JournalTab est rendu TEL QUEL — pas une ligne modifiée dedans.
import JournalTab from "./JournalTab";
import ResaV2Modal from "../../modals/ResaV2Modal";
import { formatDateFR, nightsBetween, fmtE } from "../../lib/format";
import { getAllLogsFlat } from "../../lib/sousloc-helpers";
// Micro-lot admin (2026-08-01) — extrait de liste SANS HTML brut : helper pur
// d'affichage (strip balises + entités + troncature). Le body STOCKÉ reste la
// source de vérité ; la conversation ouverte (ThreadDisplay/inbox-media) garde
// son propre traitement.
import { deriveInboxPreview } from "../../lib/inbox-preview";
import { reasonLabelFR } from "../../lib/inbox-escalation-labels";
import { useIsMobile } from "../../lib/useIsMobile";
// Lot AUTO-REFRESH — realtime « signal → refetch » (jamais de mutation d'état
// depuis le payload), le polling 60 s reste en filet (espacé si connecté).
import { useRealtimeRefresh } from "../../lib/use-realtime-refresh";

// Harmonisation statuts (Dimitri 2026-07-09) : UN SEUL langage visuel — la
// pastille de couleur (rouge = non lu, jaune/orange = à traiter), partout :
// filtres, compteurs d'en-tête, dots des threads. Le drapeau 🚩 est retiré.
// `dot` = clé DS résolue au rendu (danger/warning).
const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "unread", label: "Non lus", dot: "danger" },
  { id: "todo", label: "À traiter", dot: "warning" },
  { id: "today", label: "Aujourd'hui" },
];

// Filtre période : fenêtre glissante sur lastAt du thread (plus récents d'abord
// par défaut côté tri — cf. helper threads). `days: null` = pas de borne (tout).
const PERIODS = [
  { id: "7", label: "7 derniers jours", days: 7 },
  { id: "30", label: "30 derniers jours", days: 30 },
  { id: "all", label: "Tout", days: null },
];

// Lot AUTO-REFRESH (audit perf §5/QW5) — « Inbox allégée » : colonnes
// EXPLICITES au lieu de select("*"). Le refetch 60 s re-téléchargeait ~396 KB,
// dont les corps HTML complets des emails (`body_html`) que NI la liste NI la
// vue thread ne rendent (ThreadDisplay affiche `body` texte). Consommateurs
// vérifiés : ThreadRow/threads (thread_id, created_at, direction, read_at, id,
// sender_*, subject, body aperçu 80c, canal, reservation_id, logement_id),
// ThreadDisplay (body, subject, direction, read_at, related_send_id,
// metadata.beds24_source), InboxComposer.lastInbound (canal, sender_email,
// subject, metadata.message_id / in_reply_to), markThreadAsRead (id,
// direction, read_at). EXCLUS : body_html, external_id, replied_at,
// pricing_block_id (jamais lus côté client).
const INBOX_MESSAGES_COLUMNS =
  "id, thread_id, direction, canal, reservation_id, logement_id, " +
  "sender_email, sender_name, subject, body, read_at, created_at, " +
  "related_send_id, metadata";

// Polling filet : 60 s sans realtime (historique), espacé à 180 s quand le
// realtime est PROUVÉ vivant (les événements déclenchent le refetch en ~2 s ;
// le polling ne sert plus qu'à rattraper un trou de connexion silencieux).
// M1 (passe corrective) — l'espacement est MÉRITÉ par une PREUVE DE LIVRAISON,
// pas par le statut du canal : `connected` (SUBSCRIBED) ne prouve rien — un
// rollback de publication (ALTER PUBLICATION … DROP TABLE) laisse le canal
// SUBSCRIBED sans plus rien livrer. Critère : connected ∧ dernier événement
// reçu il y a < 15 min. Direction sûre : un jour calme sans événements →
// re-polling à 60 s (sur-polling léger, JAMAIS de sous-polling).
export const INBOX_POLL_MS = 60_000;
export const INBOX_POLL_CONNECTED_MS = 180_000;
export const INBOX_RT_PROOF_MAX_AGE_MS = 15 * 60_000;

// destinationThreadId (lot 6, prop ADDITIVE) : sélection ciblée d'un fil à
// l'arrivée (compteurs dépliables du bandeau). Consommée UNE fois.
export default function InboxPanel({ cfg, onUnreadChange, destinationThreadId = null, onDestinationConsommee }) {
  // Responsive (Lot 0) — le layout 3-panneaux ne tient pas sous md (768) : on
  // bascule en navigation à une colonne liste → conversation → réservation.
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'thread' | 'recap'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reservations, setReservations] = useState({}); // id → résa pour enrichissement display
  const [logements, setLogements] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // BUG 52 (WAGON G) — filtres bandeau : par logement + par période.
  const [logementFilter, setLogementFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  // Bascule de vue : 'flat' (tout dans l'ordre, logements mélangés, tri lastAt
  // desc — DÉFAUT, décision Dimitri V3 : l'inbox s'ouvre comme une vraie boîte
  // de réception, le plus récent en premier) / 'grouped' (par logement, filtre
  // optionnel). Les filtres existants (chips + logement + période) s'appliquent
  // dans les deux vues.
  const [viewMode, setViewMode] = useState("flat");
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  useEffect(() => {
    if (!destinationThreadId) return
    setSelectedThreadId(destinationThreadId)
    setMobileView("thread")
    if (onDestinationConsommee) onDestinationConsommee()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationThreadId])
  // Phase 2.G.5 — ouverture Fiche Résa V2 depuis le récap résa du thread
  const [selectedResaId, setSelectedResaId] = useState(null);
  // Chantier 12 LOT 1 — escalades IA NON résolues : Map<thread_id, {id, reason_code}>.
  // Alimentée via l'endpoint admin inbox_ia_escalations_list (0 texte libre).
  const [escalationsByThread, setEscalationsByThread] = useState(
    () => new Map(),
  );
  const [resolvingThreadId, setResolvingThreadId] = useState(null);

  // PR-A réactivité (Dimitri) : `silent=true` = passage AUTO (60 s) — mêmes
  // lectures Supabase, sans basculer l'état loading (pas de clignotement).
  // `silent !== true` strict : un event de clic accidentel ne rend jamais
  // le refresh silencieux.
  const refresh = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    // Pessimist #950 : en mode silencieux, l'échec ne pilote PAS la bannière
    // d'erreur (sinon scintillement toutes les 60 s en cas de panne durable —
    // on garde la dernière vue valide et on logue).
    if (silent !== true) setError(null);
    try {
      const [msgRes, rsvRes] = await Promise.all([
        supabase
          .from("inbox_messages")
          .select(INBOX_MESSAGES_COLUMNS)
          .order("created_at", { ascending: false })
          .limit(500),
        // Phase 2.G.5 — élargi pour le récap résa : status, total_amount, guest_count, payment_received_at, caution_received_at
        // Heures voyageur (formulaire de bienvenue) : guest_arrival_time /
        // guest_departure_time affichées dans le bandeau InboxThread — surface
        // ADMIN (ADR-226 : le proprio a sa propre requête cloisonnée, jamais ces colonnes).
        supabase
          .from("reservations")
          .select(
            "id, guest_first_name, guest_last_name, guest_email, logement_id, start_date, end_date, beds24_booking_id, source, status, total_amount, guest_count, payment_received_at, caution_received_at, guest_arrival_time, guest_departure_time",
          ),
      ]);
      if (msgRes.error) throw msgRes.error;
      if (rsvRes.error) throw rsvRes.error;
      setMessages(msgRes.data || []);
      const idx = {};
      (rsvRes.data || []).forEach((r) => {
        idx[r.id] = r;
      });
      setReservations(idx);
    } catch (e) {
      if (silent !== true) setError(e.message);
      else console.error("[inbox] auto-refresh silencieux en échec:", e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Chantier 12 LOT 1 — charge les escalades IA non résolues (file « à traiter »).
  // Endpoint admin requireAdmin (0 texte). Best-effort : un échec ne casse pas
  // l'Inbox (la file 🚩 disparaît simplement, les threads restent consultables).
  const refreshEscalations = useCallback(async () => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) return;
      const res = await fetch(
        "/api/beds24-mapping?action=inbox_ia_escalations_list",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "inbox_ia_escalations_list" }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) return;
      const map = new Map();
      for (const e of json.escalations || []) {
        // 1 entrée par thread : la plus récente d'abord (liste triée desc côté
        // serveur) → on garde la 1re rencontrée pour le drapeau + tooltip.
        if (e.thread_id && !map.has(e.thread_id)) {
          map.set(e.thread_id, { id: e.id, reason_code: e.reason_code });
        }
      }
      setEscalationsByThread(map);
    } catch (e) {
      console.error("[InboxPanel] refreshEscalations", e);
    }
  }, []);

  // Marque un thread comme traité (résout TOUTES ses escalades ouvertes côté
  // serveur via thread_id). Optimiste (retrait local immédiat) + refetch.
  const resolveEscalation = useCallback(
    async (threadId) => {
      if (!threadId) return;
      setResolvingThreadId(threadId);
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (!token) {
          setError("Pas de session admin active — reconnectez-vous.");
          return;
        }
        // Optimiste : retire le thread de la file avant le round-trip.
        setEscalationsByThread((prev) => {
          const next = new Map(prev);
          next.delete(threadId);
          return next;
        });
        const res = await fetch(
          "/api/beds24-mapping?action=inbox_ia_escalation_resolve",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              action: "inbox_ia_escalation_resolve",
              thread_id: threadId,
            }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok)
          throw new Error(json.error || `HTTP ${res.status}`);
        // Refetch pour réaligner sur la vérité serveur (idempotent).
        await refreshEscalations();
      } catch (e) {
        setError(e.message);
        // Échec → réaligne (le thread peut revenir dans la file).
        await refreshEscalations();
      } finally {
        setResolvingThreadId(null);
      }
    },
    [refreshEscalations],
  );

  const [bootstrapping, setBootstrapping] = useState(false);
  // Mini-lot Journal : affichage plein écran de JournalTab (composant intact).
  const [showJournal, setShowJournal] = useState(false);
  // PR-A réactivité : menu « ⋯ » (Refresh complet 90 j replié).
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  // Trigger un pull complet 90 jours côté serveur (bootstrap initial ou
  // reprise après bug — cf. fix lookback du PR #47). Auth admin JWT.
  const bootstrapPull = useCallback(async () => {
    setBootstrapping(true);
    setBootstrapResult(null);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token)
        throw new Error("Pas de session admin active — reconnectez-vous.");
      const res = await fetch(
        "/api/cron/beds24-sync?job=inbox_pull&lookback_days=90",
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await res.json().catch(() => ({}));
      // Lot L2 (P-3.37, 30/10) : la phrase du serveur d'abord (Beds24 en
      // sommeil), jamais le code brut quand une phrase existe.
      if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
      setBootstrapResult(json.stats || json);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBootstrapping(false);
    }
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  // Lot AUTO-REFRESH — realtime inbox_messages : un événement (insert cron
  // Beds24 / webhook Resend / read_at) = un signal « refetch silencieux »
  // (debounce ≥2 s, pause onglet masqué, teardown/reconnexion gérés par le
  // hook). Même garde Journal que le polling. Jamais de mutation d'état
  // depuis le payload.
  const { connected: rtConnected, lastEventAt: rtLastEventAt } = useRealtimeRefresh(
    ["inbox_messages"],
    useCallback(() => {
      if (showJournal) return;
      refresh(true);
    }, [refresh, showJournal]),
  );
  // PR-A réactivité : AUTO-REFRESH d'écran périodique (relecture Supabase
  // seule — le pull Beds24 reste côté serveur). En pause quand l'onglet
  // navigateur est masqué ou quand le Journal plein écran est ouvert.
  // Lot AUTO-REFRESH + M1 : le polling RESTE le filet de sécurité. Le tick
  // tourne TOUJOURS à 60 s et décide à CHAQUE passage (pas au setup — sinon
  // une preuve périmée figerait un intervalle 180 s) : espacement à 180 s
  // UNIQUEMENT si canal connecté ET livraison prouvée < 15 min ; sinon 60 s
  // (comportement historique — couvre : publication pas encore appliquée,
  // rollback de publication, canal muet). Le resserrage est donc automatique
  // en ≤ 15 min sans redeploy.
  const lastAutoPollAtRef = useRef(Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (showJournal) return;
      const now = Date.now();
      const rtProven =
        rtConnected &&
        rtLastEventAt != null &&
        now - rtLastEventAt < INBOX_RT_PROOF_MAX_AGE_MS;
      const period = rtProven ? INBOX_POLL_CONNECTED_MS : INBOX_POLL_MS;
      // Tolérance 1 s : les ticks tombent à ~n×60 s, sans marge un jitter
      // négatif ferait sauter le tick attendu (180 s → 240 s).
      if (now - lastAutoPollAtRef.current < period - 1_000) return;
      lastAutoPollAtRef.current = now;
      refresh(true);
    }, INBOX_POLL_MS);
    return () => clearInterval(id);
  }, [refresh, showJournal, rtConnected, rtLastEventAt]);
  // LOT 1 — charge la file d'escalades une fois au montage.
  useEffect(() => {
    refreshEscalations();
  }, [refreshEscalations]);
  // BUG 52 (WAGON G) — getAllLogsFlat descend dans cfg.props[].logs[].chambres[]
  // (chambres sous-loc lc1/lc2/lc3 de p6 "Maison Zen") et dédup par id. Sans lui,
  // le flatMap plat omettait les chambres → résolution `logements.find(l.id===…)`
  // en échec → "(logement inconnu)" sur 32/34 threads (anti-pattern maison n°4).
  useEffect(() => {
    setLogements(
      getAllLogsFlat(cfg).map((l) => ({
        id: l.id,
        nom: l.nom,
        prop: l.propNom,
      })),
    );
  }, [cfg]);

  // Group messages par thread_id
  const threads = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      if (!map.has(m.thread_id))
        map.set(m.thread_id, {
          thread_id: m.thread_id,
          messages: [],
          lastAt: 0,
          unreadCount: 0,
        });
      const t = map.get(m.thread_id);
      t.messages.push(m);
      const ts = new Date(m.created_at).getTime();
      if (ts > t.lastAt) t.lastAt = ts;
      if (m.direction === "inbound" && !m.read_at) t.unreadCount++;
    }
    // Tri chronologique des messages dans chaque thread
    map.forEach((t) =>
      t.messages.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      ),
    );
    // Tri threads par lastAt desc
    return Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
  }, [messages]);

  // Résolveur logement d'un thread — même logique que sectionsByLogement
  // (lastMsg.logement_id prioritaire, fallback resa.logement_id).
  const threadLogId = useCallback(
    (t) => {
      const lastMsg = t.messages[t.messages.length - 1];
      const resa = lastMsg?.reservation_id
        ? reservations[lastMsg.reservation_id]
        : null;
      return lastMsg?.logement_id || resa?.logement_id || null;
    },
    [reservations],
  );

  // Threads filtrés
  const filteredThreads = useMemo(() => {
    let r = threads;
    if (filter === "unread") r = r.filter((t) => t.unreadCount > 0);
    // LOT 1 — « 🚩 À traiter » : threads avec une escalade IA non résolue.
    if (filter === "todo")
      r = r.filter((t) => escalationsByThread.has(t.thread_id));
    if (filter === "today") {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      r = r.filter((t) => t.lastAt >= todayStart.getTime());
    }
    // Filtre par logement (sélecteur dédié, indépendant des chips Tous/Non lus/…)
    if (logementFilter !== "all") {
      r = r.filter((t) => threadLogId(t) === logementFilter);
    }
    // Filtre période : fenêtre glissante sur lastAt (les threads sont déjà triés
    // plus récents d'abord par le helper threads).
    const period = PERIODS.find((p) => p.id === periodFilter);
    if (period && period.days != null) {
      const cutoff = Date.now() - period.days * 24 * 60 * 60 * 1000;
      r = r.filter((t) => t.lastAt >= cutoff);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((t) => {
        const lastMsg = t.messages[t.messages.length - 1];
        const resa = lastMsg?.reservation_id
          ? reservations[lastMsg.reservation_id]
          : null;
        const fields = [
          lastMsg?.sender_email,
          lastMsg?.sender_name,
          lastMsg?.subject,
          lastMsg?.body,
          resa?.guest_first_name,
          resa?.guest_last_name,
          resa?.guest_email,
          resa?.logement_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return fields.includes(q);
      });
    }
    return r;
  }, [
    threads,
    filter,
    search,
    reservations,
    logementFilter,
    periodFilter,
    threadLogId,
    escalationsByThread,
  ]);

  const selected =
    filteredThreads.find((t) => t.thread_id === selectedThreadId) ||
    threads.find((t) => t.thread_id === selectedThreadId);

  // Auto-sélection du 1er thread si pas de sélection courante
  useEffect(() => {
    if (!selectedThreadId && filteredThreads.length > 0) {
      setSelectedThreadId(filteredThreads[0].thread_id);
    }
  }, [selectedThreadId, filteredThreads]);

  async function markThreadAsRead(threadId) {
    // Bug fix Phase 2.3 commit 5 : utiliser threadId paramètre (le state
    // `selected` n'est pas encore à jour si on vient de cliquer sur un
    // nouveau thread — setSelectedThreadId est asynchrone).
    const target = threads.find((t) => t.thread_id === threadId);
    const unread = (target?.messages || [])
      .filter((m) => m.direction === "inbound" && !m.read_at)
      .map((m) => m.id);
    if (unread.length === 0) return;
    const nowIso = new Date().toISOString();
    const { error: e } = await supabase
      .from("inbox_messages")
      .update({ read_at: nowIso })
      .in("id", unread);
    if (!e) {
      // Optimistic local update : éviter un round-trip refresh
      setMessages((prev) =>
        prev.map((m) =>
          unread.includes(m.id) ? { ...m, read_at: nowIso } : m,
        ),
      );
      // Refresh badge top sous-onglet (cf. MessagesPanel)
      if (typeof onUnreadChange === "function") onUnreadChange();
    }
  }

  const totalUnread = useMemo(
    () => threads.reduce((acc, t) => acc + t.unreadCount, 0),
    [threads],
  );
  // LOT 1 — nombre de threads escaladés non résolus visibles dans la file.
  const totalTodo = useMemo(
    () =>
      threads.reduce(
        (acc, t) => acc + (escalationsByThread.has(t.thread_id) ? 1 : 0),
        0,
      ),
    [threads, escalationsByThread],
  );

  // Options du sélecteur logement, triées par id (lc1/lc2/lc3 inclus via getAllLogsFlat).
  const logementOptions = useMemo(
    () =>
      [...logements].sort((a, b) => String(a.id).localeCompare(String(b.id))),
    [logements],
  );

  // Groupement par logement (Phase 2.3 UX) :
  // - 1 section par logement (nom complet via cfg.props[].logs[].nom)
  // - Section "(logement inconnu)" en dernier pour les threads sans match
  // - Sections triées par date du dernier message (logement le plus actif en haut)
  // - Threads dans chaque section déjà triés par lastAt desc (cf. helper threads)
  const sectionsByLogement = useMemo(() => {
    const map = new Map();
    for (const t of filteredThreads) {
      const lastMsg = t.messages[t.messages.length - 1];
      const resa = lastMsg?.reservation_id
        ? reservations[lastMsg.reservation_id]
        : null;
      const logId = lastMsg?.logement_id || resa?.logement_id || null;
      const log = logId ? logements.find((l) => l.id === logId) : null;
      const key = logId || "__unknown__";
      if (!map.has(key)) {
        map.set(key, {
          key,
          logId,
          logName: log ? `${log.id} · ${log.nom}` : "(logement inconnu)",
          isUnknown: !log,
          threads: [],
          lastAt: 0,
          unreadCount: 0,
        });
      }
      const sec = map.get(key);
      sec.threads.push(t);
      if (t.lastAt > sec.lastAt) sec.lastAt = t.lastAt;
      sec.unreadCount += t.unreadCount;
    }
    // Tri sections : actifs en haut, "(logement inconnu)" toujours en dernier
    return Array.from(map.values()).sort((a, b) => {
      if (a.isUnknown && !b.isUnknown) return 1;
      if (b.isUnknown && !a.isUnknown) return -1;
      return b.lastAt - a.lastAt;
    });
  }, [filteredThreads, reservations, logements]);

  // Mini-lot Journal : vue plein écran (JournalTab TEL QUEL, bouton retour).
  if (showJournal) {
    return (
      <div style={{ fontFamily: DS.fontBody }}>
        <button
          className="fbz-press"
          data-testid="journal-back-btn"
          onClick={() => setShowJournal(false)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: DS.card, color: DS.textSecondary, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: DS.fontBody, marginBottom: 12 }}
        >
          <ArrowLeft size={14} strokeWidth={2}/> Retour aux messages
        </button>
        <JournalTab cfg={cfg} />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Header global */}
      <div
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 14,
          marginBottom: 14,
          border: `0.5px solid ${DS.border}`,
          boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontFamily: DS.fontDisplay,
                fontSize: 16.5,
                fontWeight: 600,
                color: DS.marineDark,
                flexWrap: "wrap",
              }}
            >
              <InboxIcon size={17} strokeWidth={2} color={DS.marine}/> <span title="Points de statut : 🔴 non lu · 🟠 à traiter · ⚪ lu (visible au survol de chaque point)">Message · {threads.length}</span>
              {totalUnread > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    color: DS.corail,
                    fontWeight: 700,
                  }}
                >
                  · <span style={{ width: 7, height: 7, borderRadius: "50%", background: DS.corail, display: "inline-block" }}/> {totalUnread} non lu{totalUnread > 1 ? "s" : ""}
                </span>
              )}
              {totalTodo > 0 && (
                <span
                  data-testid="inbox-todo-counter"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: 12,
                    color: DS.corail,
                    fontWeight: 700,
                  }}
                >
                  · <span style={{ width: 7, height: 7, borderRadius: "50%", background: DS.warning, display: "inline-block" }}/> {totalTodo} à traiter (escalades du copilote)
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              className="fbz-press"
              data-testid="journal-open-btn"
              onClick={() => setShowJournal(true)}
              title="Prochains envois projetés + historique des traces (lecture seule)"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: DS.card,
                color: DS.marine,
                border: `0.5px solid ${DS.borderHover}`,
                borderRadius: DS.radiusControl,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              <History size={13} strokeWidth={2}/> Journal
            </button>
            {/* PR-A réactivité : « Rafraîchir (local) » SUPPRIMÉ — l'écran se
                met à jour tout seul (60 s). « Refresh complet (90 j) » vit
                dans le menu « ⋯ » (usage exceptionnel, fonction intacte). */}
            {/* Lot AUTO-REFRESH — texte d'aide = état réel (règle Dimitri) :
                temps réel quand le canal est connecté, sinon filet 60 s. */}
            <span data-testid="inbox-autorefresh-hint" style={{ color: DS.textMuted, alignSelf: "center", display: "inline-flex" }} title={rtConnected ? "Mise à jour automatique en temps réel (filet toutes les 3 minutes) — aucun bouton à cliquer." : "Mise à jour automatique toutes les 60 secondes — aucun bouton à cliquer."}>
              <RefreshCw size={13} strokeWidth={2}/>
            </span>
            <div style={{ position: "relative" }}>
              <button
                className="fbz-press fbz-tap"
                aria-label="Plus d'actions"
                data-testid="inbox-more-btn"
                onClick={() => setMoreMenuOpen((v) => !v)}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, background: moreMenuOpen ? DS.cielSoft : DS.card, color: DS.textSecondary, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, cursor: "pointer" }}
              >
                ⋯
              </button>
              {moreMenuOpen && (
                <>
                  <div onClick={() => setMoreMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }}/>
                  <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 31, minWidth: 230, background: DS.card, border: `0.5px solid ${DS.border}`, borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,.14)", padding: 5, fontFamily: DS.fontBody }}>
                    <button
                      className="fbz-rowhover"
                      data-testid="inbox-pull90-btn"
                      onClick={() => { setMoreMenuOpen(false); bootstrapPull(); }}
                      disabled={bootstrapping}
                      title="Force un pull Beds24 sur 90 jours (bootstrap initial ou rattrapage). Appel cron manuel via admin JWT."
                      style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: 7, padding: "8px 10px", fontSize: 12.5, fontWeight: 500, color: DS.warningText, cursor: bootstrapping ? "wait" : "pointer", fontFamily: DS.fontBody }}
                    >
                      <RefreshCw size={14} strokeWidth={2}/> {bootstrapping ? "Récupération en cours…" : "Tout resynchroniser (90 jours)"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {bootstrapResult && (
          <div
            style={{
              display: "flex",
              gap: 6,
              margin: "6px 0",
              padding: "6px 10px",
              background: DS.successBg,
              color: DS.successText,
              borderRadius: 6,
              fontSize: 11,
              fontFamily: "monospace",
            }}
          >
            {/* Les mots de la boîte (lot 6) + les gardes `?? 0` de main (paquet
                comportement) : un compteur absent affiche 0, jamais « undefined ». */}
            <CheckCircle2 size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }}/> <span>Récupération 90 jours : {bootstrapResult.bookings_polled ?? 0} réservation(s),{" "}
            {bootstrapResult.messages_fetched_raw ?? 0} relevés bruts (guest:
            {bootstrapResult.messages_by_source?.guest || 0} / host:
            {bootstrapResult.messages_by_source?.host || 0}),
            {bootstrapResult.messages_inserted ?? 0} insérés,{" "}
            {bootstrapResult.messages_skipped_dup ?? 0} doublons</span>
          </div>
        )}

        {/* En-tête 2 lignes (proposition validée Dimitri) — LIGNE 2 : barre de
            filtres UNIQUE, toujours visible : recherche + segmenté statut
            (grammaire des rails) + selects + bascule de vue à droite. */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
            marginTop: 8,
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (voyageur, sujet, logement)…"
            style={{
              flex: "1 1 200px",
              minWidth: 0,
              boxSizing: "border-box",
              border: `0.5px solid ${DS.border}`,
              borderRadius: DS.radiusControl,
              padding: "6px 10px",
              fontSize: 12,
              outline: "none",
              background: DS.card,
              color: DS.textPrimary,
              fontFamily: DS.fontBody,
            }}
          />
          <div data-testid="inbox-status-filter" style={{ display: "flex", background: DS.pageBg, borderRadius: 9, padding: 3, gap: 2 }}>
            {FILTERS.map((f) => {
              const on = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  className="fbz-press"
                  onClick={() => setFilter(f.id)}
                  style={{
                    border: "none",
                    background: on ? DS.card : "transparent",
                    color: on ? DS.marine : DS.textMuted,
                    boxShadow: on ? "0 1px 3px rgba(20,51,80,.14)" : "none",
                    borderRadius: 6,
                    padding: "5px 10px",
                    fontFamily: DS.fontBody,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {f.dot && (
                    <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: DS[f.dot], flexShrink: 0 }}/>
                  )}
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* BUG 52 (WAGON G) — filtre par logement (alimenté par getAllLogsFlat,
              inclut les chambres sous-loc lc1/lc2/lc3). */}
          <select
            aria-label="Filtrer par logement"
            value={logementFilter}
            onChange={(e) => setLogementFilter(e.target.value)}
            style={{
              padding: "4px 8px",
              border: "1px solid #dde3ec",
              borderRadius: 6,
              background: logementFilter !== "all" ? DS.marine : DS.card,
              color: logementFilter !== "all" ? "#fff" : DS.marine,
              fontWeight: 600,
              fontSize: 11,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              outline: "none",
            }}
          >
            <option value="all">Tous les logements</option>
            {logementOptions.map((l) => (
              <option key={l.id} value={l.id}>
                {l.id} · {l.nom}
              </option>
            ))}
          </select>

          {/* Filtre/tri par période (plus récents d'abord par défaut). */}
          <select
            aria-label="Filtrer par période"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            style={{
              padding: "4px 8px",
              border: "1px solid #dde3ec",
              borderRadius: 6,
              background: periodFilter !== "all" ? DS.marine : DS.card,
              color: periodFilter !== "all" ? "#fff" : DS.marine,
              fontWeight: 600,
              fontSize: 11,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              outline: "none",
            }}
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>

          {/* Ajustement Vague 1 — bascule de vue (2 boutons segmentés). */}
          <div
            role="group"
            aria-label="Mode d'affichage"
            style={{
              display: "inline-flex",
              marginLeft: "auto",
              border: `0.5px solid ${DS.border}`,
              borderRadius: DS.radiusControl,
              overflow: "hidden",
            }}
          >
            <button
              className="fbz-press"
              aria-pressed={viewMode === "grouped"}
              onClick={() => setViewMode("grouped")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 11px",
                border: "none",
                background: viewMode === "grouped" ? DS.marine : DS.card,
                color: viewMode === "grouped" ? "#fff" : DS.textSecondary,
                fontWeight: 600,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              <Home size={13} strokeWidth={2}/> Par logement
            </button>
            <button
              className="fbz-press"
              aria-pressed={viewMode === "flat"}
              onClick={() => setViewMode("flat")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 11px",
                border: "none",
                borderLeft: `0.5px solid ${DS.border}`,
                background: viewMode === "flat" ? DS.marine : DS.card,
                color: viewMode === "flat" ? "#fff" : DS.textSecondary,
                fontWeight: 600,
                fontSize: 11,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              <Clock3 size={13} strokeWidth={2}/> Tout dans l'ordre
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 12px",
            background: DS.dangerBg,
            color: DS.dangerText,
            borderRadius: 8,
            borderLeft: `3px solid ${DS.danger}`,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0 }}/> {error}
        </div>
      )}

      {/* Layout 3 colonnes (Phase 2.G.5) : threads / thread courant / récap résa.
          Sous md (Lot 0 responsive) : 1 colonne navigable (liste/thread/résa). */}
      <div
        style={
          isMobile
            ? { display: "block" }
            : {
                display: "grid",
                gridTemplateColumns:
                  "minmax(260px, 320px) minmax(0, 1fr) 320px",
                gap: 14,
                minHeight: 480,
                alignItems: "start",
              }
        }
      >
        {/* Liste threads */}
        <div
          style={{
            background: DS.white,
            borderRadius: 12,
            border: `0.5px solid ${DS.border}`,
          boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
            overflow: "hidden",
            maxHeight: "calc(100vh - 260px)",
            overflowY: "auto",
            ...(isMobile && mobileView !== "list" ? { display: "none" } : {}),
          }}
        >
          {loading && filteredThreads.length === 0 && (
            <div
              style={{
                padding: 14,
                color: "#888",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              Chargement…
            </div>
          )}
          {!loading && filteredThreads.length === 0 && (
            <div
              style={{
                padding: 18,
                color: "#888",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              Aucun thread
              {filter !== "all" ||
              logementFilter !== "all" ||
              periodFilter !== "all"
                ? " pour ces filtres"
                : ""}
              .
            </div>
          )}
          {/* Vue groupée (défaut) : une section par logement. */}
          {viewMode === "grouped" &&
            sectionsByLogement.map((sec) => (
              <div key={sec.key} data-testid="inbox-thread-section">
                <div
                  style={{
                    padding: "8px 12px",
                    background: sec.isUnknown ? DS.pageBg : DS.cielSoft,
                    borderBottom: `1px solid ${DS.border}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontFamily: DS.fontDisplay,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: sec.isUnknown ? DS.textMuted : DS.marineDark,
                        letterSpacing: 0.2,
                      }}
                    >
                      <Home size={13} strokeWidth={2}/> {sec.logName}
                    </span>
                    <span style={{ fontSize: 10, color: DS.textMuted }}>
                      · {sec.threads.length} thread
                      {sec.threads.length > 1 ? "s" : ""}
                    </span>
                    {sec.unreadCount > 0 && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          marginLeft: "auto",
                          fontSize: 10,
                          color: DS.corail,
                          fontWeight: 700,
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: DS.corail, display: "inline-block" }}/> {sec.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
                {sec.threads.map((t) => (
                  <ThreadRow
                    key={t.thread_id}
                    t={t}
                    reservations={reservations}
                    logements={logements}
                    escalation={escalationsByThread.get(t.thread_id)}
                    isSelected={t.thread_id === selectedThreadId}
                    onSelect={() => {
                      setSelectedThreadId(t.thread_id);
                      markThreadAsRead(t.thread_id);
                      setMobileView("thread");
                    }}
                  />
                ))}
              </div>
            ))}

          {/* Vue plate "Tout dans l'ordre" : liste plate de TOUS les threads
              (logements mélangés), déjà triée lastAt desc par le helper threads.
              Chaque ThreadRow garde son badge logement. */}
          {viewMode === "flat" && filteredThreads.length > 0 && (
            <div data-testid="inbox-thread-section">
              {[...filteredThreads]
                .sort(
                  (a, b) => new Date(b.lastAt || 0) - new Date(a.lastAt || 0),
                )
                .map((t) => (
                  <ThreadRow
                    key={t.thread_id}
                    t={t}
                    reservations={reservations}
                    logements={logements}
                    escalation={escalationsByThread.get(t.thread_id)}
                    isSelected={t.thread_id === selectedThreadId}
                    onSelect={() => {
                      setSelectedThreadId(t.thread_id);
                      markThreadAsRead(t.thread_id);
                      setMobileView("thread");
                    }}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Thread sélectionné */}
        <div
          style={{
            background: DS.white,
            borderRadius: 12,
            border: `0.5px solid ${DS.border}`,
          boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
            overflow: "hidden",
            minHeight: 480,
            ...(isMobile && mobileView !== "thread" ? { display: "none" } : {}),
          }}
        >
          {/* Nav mobile (Lot 0) : retour liste + accès récap résa. */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderBottom: "1px solid #eef2f7",
                background: "#fafbfd",
              }}
            >
              <button
                className="fbz-tap"
                onClick={() => setMobileView("list")}
                style={{
                  background: "#eef2f7",
                  color: DS.navy,
                  border: "none",
                  borderRadius: 7,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ← Conversations
              </button>
              <button
                className="fbz-tap"
                onClick={() => setMobileView("recap")}
                style={{
                  background: "#eef2f7",
                  color: DS.navy,
                  border: "none",
                  borderRadius: 7,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Réservation ▸
              </button>
            </div>
          )}
          {/* LOT 1 — bandeau « à traiter » du thread courant + résolution.
              Affiché uniquement si le thread sélectionné a une escalade ouverte.
              Purement informatif : ne bloque jamais la réponse (composer actif). */}
          {selected && escalationsByThread.has(selected.thread_id) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                background: DS.warningBg,
                borderBottom: `1px solid ${DS.warning}`,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                  color: DS.warningText,
                  fontWeight: 700,
                  flex: 1,
                }}
              >
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: DS.warning, flexShrink: 0 }}/> À traiter —{" "}
                {reasonLabelFR(
                  escalationsByThread.get(selected.thread_id)?.reason_code,
                )}
              </span>
              <button
                data-testid="ia-resolve-btn"
                onClick={() => resolveEscalation(selected.thread_id)}
                disabled={resolvingThreadId === selected.thread_id}
                style={{
                  background: DS.navy,
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "5px 12px",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor:
                    resolvingThreadId === selected.thread_id
                      ? "wait"
                      : "pointer",
                  opacity: resolvingThreadId === selected.thread_id ? 0.6 : 1,
                }}
              >
                {resolvingThreadId === selected.thread_id
                  ? "…"
                  : <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><CheckCircle2 size={13} strokeWidth={2}/> Marquer traité</span>}
              </button>
            </div>
          )}
          {selected ? (
            // key={thread_id} : force le remount d'InboxThread (et de son
            // InboxComposer enfant) au changement de thread → l'état local du
            // composer (brouillon, confirmation d'envoi) et la présélection
            // dérivée des props se réinitialisent. Sans key, React réconcilie
            // le même composant et l'état figé fuit d'un thread à l'autre.
            <InboxThread
              key={selected.thread_id}
              thread={selected}
              reservations={reservations}
              logements={logements}
              onComposerSent={refresh}
            />
          ) : (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "#888",
                fontSize: 13,
              }}
            >
              Sélectionnez une conversation à gauche.
            </div>
          )}
        </div>

        {/* Récap résa associée (Phase 2.G.5) */}
        <div
          style={{
            background: DS.white,
            borderRadius: 12,
            border: `0.5px solid ${DS.border}`,
          boxShadow: "0 10px 30px -14px rgba(20,51,80,.22)",
            overflow: "hidden",
            minHeight: 480,
            maxHeight: "calc(100vh - 260px)",
            overflowY: "auto",
            ...(isMobile && mobileView !== "recap" ? { display: "none" } : {}),
          }}
        >
          {/* Nav mobile (Lot 0) : retour à la conversation. */}
          {isMobile && (
            <div
              style={{
                padding: "6px 10px",
                borderBottom: "1px solid #eef2f7",
                background: "#fafbfd",
              }}
            >
              <button
                className="fbz-tap"
                onClick={() => setMobileView("thread")}
                style={{
                  background: "#eef2f7",
                  color: DS.navy,
                  border: "none",
                  borderRadius: 7,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ← Conversation
              </button>
            </div>
          )}
          {selected ? (
            <ResaRecap
              thread={selected}
              reservations={reservations}
              logements={logements}
              onOpenFiche={setSelectedResaId}
            />
          ) : (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: "#888",
                fontSize: 13,
              }}
            >
              —
            </div>
          )}
        </div>
      </div>

      {/* Modale Fiche Résa V2 (Phase 2.G.5 trigger depuis récap)
          PR #169a-bis (ADR-105) — passe itemId + itemKind explicites
          (alias legacy reservationId déprécié). */}
      {selectedResaId && (
        <ResaV2Modal
          itemId={selectedResaId}
          itemKind="reservation"
          cfg={cfg}
          onClose={() => {
            setSelectedResaId(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// Icône de canal (DA — trait fin Lucide, remplace les emojis 📧💬🟢📨).
function CanalIcon({ canal, size = 12 }) {
  if (canal === "email") return <Mail size={size} strokeWidth={2} />;
  if (canal === "tchat_ota") return <MessageSquare size={size} strokeWidth={2} />;
  if (canal === "whatsapp") return <MessageCircle size={size} strokeWidth={2} color={DS.success} />;
  return <InboxIcon size={size} strokeWidth={2} />;
}

function ThreadRow({
  t,
  reservations,
  logements,
  escalation,
  isSelected,
  onSelect,
}) {
  const lastMsg = t.messages[t.messages.length - 1];
  const resa = lastMsg?.reservation_id
    ? reservations[lastMsg.reservation_id]
    : null;
  const log = resa?.logement_id
    ? logements.find((l) => l.id === resa.logement_id)
    : null;
  // LOT 1 — drapeau « à traiter » (escalade IA non résolue). Tooltip = libellé FR.
  const todoLabel = escalation ? reasonLabelFR(escalation.reason_code) : null;
  return (
    // Reskin Messagerie (Dimitri) : ligne = carte façon rails Direct/Plateformes
    // — fond BLANC (le dot porte le non-lu, plus de fond teinté), sélection =
    // contour marine + surélévation. AUCUNE logique touchée (onSelect intact).
    <button
      onClick={onSelect}
      className="fbz-rowhover"
      style={{
        display: "block",
        width: "calc(100% - 12px)",
        textAlign: "left",
        cursor: "pointer",
        background: DS.card,
        padding: "9px 11px",
        margin: "4px 6px",
        border: `1.5px solid ${isSelected ? DS.marine : "transparent"}`,
        borderRadius: 10,
        boxShadow: isSelected ? "0 7px 18px -8px rgba(20,51,80,.35)" : "none",
        fontFamily: DS.fontBody,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 12.5,
            fontFamily: DS.fontDisplay,
            fontWeight: t.unreadCount > 0 ? 600 : 500,
            color: DS.marineDark,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {/* Point de statut (données réelles) : ROUGE non lu > ORANGE à
              traiter (escalade IA non résolue) > GRIS lu. Harmonisation
              pastilles (Dimitri 2026-07-09) : le drapeau 🚩 est remplacé par
              une 2e pastille ORANGE qui conserve le signal « à traiter »
              même quand le dot principal est rouge non-lu. */}
          <span
            data-testid="thread-status-dot"
            title={t.unreadCount > 0 ? "Non lu" : escalation ? `À traiter — ${todoLabel}` : "Lu"}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              flexShrink: 0,
              background: t.unreadCount > 0 ? DS.danger : escalation ? DS.warning : DS.borderHover,
            }}
          />
          {escalation && t.unreadCount > 0 && (
            <span
              data-testid="ia-thread-todo-dot"
              title={`À traiter — ${todoLabel}`}
              style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: DS.warning }}
            />
          )}
          {resa
            ? `${resa.guest_first_name || "?"} ${resa.guest_last_name || ""}`.trim()
            : lastMsg?.sender_name ||
              lastMsg?.sender_email ||
              t.thread_id.slice(0, 30)}
        </span>
        <span style={{ fontSize: 10, color: DS.textMuted }}>
          {formatTimestampShort(lastMsg?.created_at)}
        </span>
      </div>
      <div
        style={{
          fontSize: 10,
          color: "#888",
          marginTop: 2,
          display: "flex",
          alignItems: "center",
          gap: 5,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 5px",
            borderRadius: 5,
            background: DS.pageBg,
            color: DS.textSecondary,
            fontWeight: 600,
          }}
        >
          <CanalIcon canal={lastMsg?.canal} />
        </span>
        {log && (
          <span>
            {log.id} · {log.nom}
          </span>
        )}
        {resa?.source === "airbnb" && (
          <span data-testid="inbox-source-badge" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, color: DS.corailText, background: DS.corailSoft, padding: "1px 6px", borderRadius: 5 }}>AIRBNB</span>
        )}
        {resa?.source === "booking" && (
          <span data-testid="inbox-source-badge" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, color: DS.marineDeep, background: DS.cielSoft, padding: "1px 6px", borderRadius: 5 }}>BOOKING</span>
        )}
        {resa?.source && resa.source !== "airbnb" && resa.source !== "booking" && <span>· {resa.source}</span>}
        {t.unreadCount > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: "auto", color: DS.corail, fontWeight: 700 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: DS.corail, display: "inline-block" }}/> {t.unreadCount}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#666",
          marginTop: 3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {lastMsg?.subject && (
          <span style={{ fontWeight: 600 }}>{lastMsg.subject} · </span>
        )}
        {/* Extrait dérivé à l'AFFICHAGE (HTML OTA brut nettoyé) — le body
            stocké n'est jamais modifié. */}
        {deriveInboxPreview(lastMsg?.body)}
      </div>
    </button>
  );
}

function formatTimestampShort(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

// ── ResaRecap (Phase 2.G.5) ───────────────────────────────────────
// Mini-fiche résa à droite du thread. Lookup par lastMsg.reservation_id
// (priorité), fallback par sender_email matchant guest_email.
// Bouton "📋 Détails" → ouvre ResaV2Modal complète.

function ResaRecap({ thread, reservations, logements, onOpenFiche }) {
  const lastMsg = thread.messages[thread.messages.length - 1];
  // 1. Priorité : reservation_id du dernier message (déjà résolu côté serveur)
  let resa = null;
  if (lastMsg?.reservation_id) {
    resa = reservations[lastMsg.reservation_id];
  }
  // 2. Fallback : matcher par sender_email parmi tous les messages inbound du thread
  if (!resa) {
    for (const m of thread.messages) {
      if (m.direction === "inbound" && m.sender_email) {
        const found = Object.values(reservations).find(
          (r) =>
            r.guest_email &&
            r.guest_email.toLowerCase() === m.sender_email.toLowerCase(),
        );
        if (found) {
          resa = found;
          break;
        }
      }
    }
  }

  if (!resa) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>
          Aucune réservation associée
        </div>
        <div style={{ fontSize: 11, color: "#aaa", lineHeight: 1.5 }}>
          Le voyageur n'a pas (encore) de résa Beds24 synchronisée
          correspondante. Cherché : <code>reservation_id</code> du thread +
          matching <code>guest_email</code>.
        </div>
      </div>
    );
  }

  const log = logements.find((l) => l.id === resa.logement_id);
  const nights = nightsBetween(resa.start_date, resa.end_date);
  const sourceColor =
    resa.source === "airbnb"
      ? DS.srcAirbnb
      : resa.source === "booking"
        ? DS.srcBooking
        : DS.srcDirect;
  const statusColor =
    resa.status === "confirmed"
      ? DS.success
      : resa.status === "cancelled"
        ? DS.danger
        : DS.warning;

  return (
    <div style={{ padding: 16, fontFamily: DS.fontBody }}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontFamily: DS.fontDisplay,
            fontSize: 15.5,
            fontWeight: 600,
            color: DS.marineDark,
            marginBottom: 4,
          }}
        >
          {`${resa.guest_first_name || "?"} ${resa.guest_last_name || ""}`.trim()}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {resa.source && (
            <span
              style={{
                fontSize: 9,
                color: "#fff",
                background: sourceColor,
                padding: "2px 7px",
                borderRadius: 4,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              {resa.source}
            </span>
          )}
          {resa.status && (
            <span title={resa.status}
              style={{
                fontSize: 9,
                color: "#fff",
                background: statusColor,
                padding: "2px 7px",
                borderRadius: 4,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.3,
              }}
            >
              {{confirmed:"Confirmée",cancelled:"Annulée",upcoming:"À venir",completed:"Terminée"}[resa.status] || resa.status}
            </span>
          )}
        </div>
      </div>

      {/* Récap */}
      <div style={{ marginBottom: 14 }}>
        <RecapRow
          label="Durée"
          value={`${nights} nuit${nights > 1 ? "s" : ""}`}
        />
        <RecapRow label="Arrivée" value={formatDateFR(resa.start_date)} />
        <RecapRow label="Départ" value={formatDateFR(resa.end_date)} />
        <RecapRow
          label="Logement"
          value={log ? log.nom : `${resa.logement_id} (?)`}
        />
        <RecapRow
          label="Voyageurs"
          value={resa.guest_count != null ? `${resa.guest_count}` : "—"}
        />
        <RecapRow
          label="Prix total"
          value={resa.total_amount != null ? fmtE(resa.total_amount) : "—"}
        />
        <RecapRow
          label="Paiement"
          value={
            resa.payment_received_at ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: DS.success, fontWeight: 600 }}><CheckCircle2 size={13} strokeWidth={2}/> reçu</span>
            ) : (
              <span style={{ color: DS.textMuted, fontStyle: "italic" }}>—</span>
            )
          }
        />
        <RecapRow
          label="Caution"
          value={
            resa.caution_received_at ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: DS.success, fontWeight: 600 }}><CheckCircle2 size={13} strokeWidth={2}/> reçue</span>
            ) : (
              <span style={{ color: DS.textMuted, fontStyle: "italic" }}>—</span>
            )
          }
        />
      </div>

      {/* Bouton Détails → Fiche Résa V2 */}
      <button
        className="fbz-press"
        onClick={() => onOpenFiche(resa.id)}
        style={{
          width: "100%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          background: DS.corail,
          color: "#fff",
          border: "none",
          borderRadius: DS.radiusControl,
          padding: "10px 14px",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: DS.fontBody,
        }}
      >
        <FileText size={14} strokeWidth={2}/> Détails de la réservation
      </button>
    </div>
  );
}

function RecapRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        borderBottom: `1px solid ${DS.border}`,
        fontSize: 12,
        gap: 8,
      }}
    >
      <span style={{ color: DS.textMuted, fontWeight: 500 }}>{label}</span>
      <span
        style={{ color: DS.textPrimary, textAlign: "right", wordBreak: "break-word" }}
      >
        {value}
      </span>
    </div>
  );
}
